/**
 * **The attack fx rule** (`attack-animations` Phase 1, `projects/attack-animations/research.md` §4):
 * which animation family an attack plays, read off the firing weapon's own frozen data. Pure; the
 * resolver stamps its answer on each `to_hit` step ({@link import("@lancer-vtt/domain").ToHitStep.fx})
 * and nothing in the rules reads it back.
 *
 * First match wins:
 *
 * 1. a tech attack → `glyph`
 * 2. resolved as melee → `strike` (`thrown` when a class-Melee weapon is thrown beyond its Threat)
 * 3. Line → `beam`
 * 4. Nexus → `swarm` (Nexus beats Cone, ruled 2026-10-10: Seeker Cloud reads as drones)
 * 5. Cone → `spray`
 * 6. Seeking → `missile`
 * 7. Burst → `spray`
 * 8. Arcing → `lobbed`
 * 9. Launcher → `missile`
 * 10. Cannon → `heavy`
 * 11. CQB → `volley`
 * 12. anything else ranged → `tracer`
 *
 * {@link FX_OVERRIDES} then replaces the family for the few weapons the rule misreads.
 */

import type { AttackFamily, AttackFx, AttackPalette, DamageType, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import { areaShapeOf } from "./resolve/area";

/**
 * The misfits the rule reads wrong (research §4 "Known misfits", ruled 2026-10-10). Keyed by the
 * weapon's compendium id. A Pistol is CQB, so the rule gives it a shotgun volley; it fires one
 * round. The machine guns are a Cannon (one heavy round), a Rifle (one tracer) and an Arcing Rifle
 * (a lobbed shell); each fires a stream, which the volley's spread of rounds reads as.
 */
export const FX_OVERRIDES: Readonly<Record<string, AttackFamily>> = {
  mw_pistol: "tracer",
  mw_heavy_machine_gun: "volley",
  npcf_light_machine_gun_archer: "volley",
  mw_oracle_lmg_i: "volley",
};

/** What {@link attackFx} reads. */
export type AttackFxInput = {
  /** The firing profile (its range, tags and damage type), the one the to-hit and damage record. */
  profile: WeaponProfile;
  /**
   * The weapon itself (the multi-profile parent): its compendium id keys {@link FX_OVERRIDES} and
   * its class word (`type`, or an NPC weapon's `fxClass`) drives rules 4 and 9 to 11.
   */
  weapon: WeaponProfile;
  /** Resolved as a tech attack (`intent.tech`). */
  tech: boolean;
  /** Resolved as a ranged attack: the attack facts' `rangedAttack`, so a Ram Cannon at Threat reads melee. */
  ranged: boolean;
  /** A class-Melee weapon thrown beyond its Threat (`tg_thrown`). */
  thrown?: boolean;
};

const hasTag = (profile: WeaponProfile, id: string): boolean => profile.tags?.some((t) => t.id === id) ?? false;

/** The palette a damage type draws in: `variable` reads as kinetic, like the mitigation fallback. */
export function paletteOf(type: DamageType | undefined): AttackPalette {
  return type === undefined || type === "variable" ? "kinetic" : type;
}

/**
 * Whether an attack the resolver did not read as ranged looks like a swing: it has a Threat, or a
 * Melee class, or no reach and no class at all (the unarmed fallback). The resolver also reads a
 * weapon with unparseable reach as not ranged (the Mimic Gun's `???`, the Apocalypse Rail's spent
 * `N/A` profile); a gun with a ranged class still fires a round.
 */
function isMeleeShaped(profile: WeaponProfile, cls: string | undefined): boolean {
  const range = profile.range ?? [];
  return range.some((r) => r.kind === "threat") || cls === "Melee" || (cls === undefined && range.length === 0);
}

/** The family by the rule, before overrides. */
function ruleFamily(input: AttackFxInput, area: ReturnType<typeof areaShapeOf>): AttackFamily {
  const { profile, weapon, tech, ranged } = input;
  if (tech) return "glyph";
  const cls = weapon.type ?? weapon.fxClass;
  if (!ranged && isMeleeShaped(profile, cls)) return "strike";
  if (area?.kind === "line") return "beam";
  if (cls === "Nexus") return "swarm";
  if (area?.kind === "cone") return "spray";
  if (hasTag(profile, "tg_seeking")) return "missile";
  if (area?.kind === "burst") return "spray";
  if (hasTag(profile, "tg_arcing")) return "lobbed";
  if (cls === "Launcher") return "missile";
  if (cls === "Cannon") return "heavy";
  if (cls === "CQB") return "volley";
  return "tracer";
}

/** The fx descriptor for one weapon or tech attack. */
export function attackFx(input: AttackFxInput): AttackFx {
  const { profile, weapon, tech, thrown } = input;
  const area = tech ? undefined : areaShapeOf(profile.range ?? []);
  const family = tech ? "glyph" : (FX_OVERRIDES[weapon.id] ?? ruleFamily(input, area));
  return {
    family,
    ...(area !== undefined ? { area } : {}),
    palette: tech ? "kinetic" : paletteOf(profile.damage.type),
    ...(family === "strike" && thrown === true ? { thrown: true as const } : {}),
    ...(!tech && profile.smart === true ? { smart: true as const } : {}),
  };
}

/**
 * The fx for a **non-weapon** area action (a Frag Grenade): the same rule over the action's frozen
 * reach, with no weapon class to read. A Line is a beam, a Cone or Burst a spray, and a Blast a
 * lobbed throw; anything else falls back to tracer. `undefined` when the action declares no reach
 * at all, which is not an attack the map should draw.
 */
export function actionFx(range: readonly WeaponRange[] | undefined, damageType: DamageType | undefined): AttackFx | undefined {
  if (range === undefined || range.length === 0) return undefined;
  const area = areaShapeOf([...range]);
  const family: AttackFamily =
    area?.kind === "line" ? "beam" : area?.kind === "cone" || area?.kind === "burst" ? "spray" : area?.kind === "blast" ? "lobbed" : "tracer";
  return { family, ...(area !== undefined ? { area } : {}), palette: paletteOf(damageType) };
}
